import React, { useCallback } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { fetchAllVenues, matchVenuesByAi, mergeVenues } from '../../../../../api/venues';
import toast from 'react-hot-toast';
import ConfigurationApiTable from '../configurationApiTable';
import { Box } from '@mui/material';
import ConfirmationDialog from '../../../../common/confirmationDialog';
import { MATCH_RESPONSE_STATUS } from '../../../../../utils/constants';
import timeout from '../../../../../utils/timeout';

const { v4: uuidv4 } = require('uuid');

const ConfigurationApiVenues = ({ sport, sources, onClose }) => {
  const [venuesHashBySource, setVenuesHashBySource] = useState({});
  const [apiConfigurationHash, setApiConfigurationHash] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const [aiMatchLoading, setAiMatchLoading] = useState(false);

  const createVenuesHash = useCallback((venues) => {
    const venuesHashBySource = venues.reduce((a, c) => {
      if (!a[c.source]) {
        a[c.source] = {};
      }
      a[c.source][c.id] = c;
      return a;
    }, {});

    const apiConfigurationHash = venues.reduce((a, c) => {
      if (!c.unionKey) {
        return a;
      }
      if (a[c.unionKey]) {
        a[c.unionKey][c.source] = { id: c.id, source: c.source, unionKey: c.unionKey };
      } else {
        a[c.unionKey] = { [c.source]: { id: c.id, source: c.source, unionKey: c.unionKey } };
      }
      return a;
    }, {});
    setVenuesHashBySource(venuesHashBySource);
    setApiConfigurationHash(apiConfigurationHash);
  }, []);

  const setupVenues = useCallback(async () => {
    if (!sources) {
      return;
    }
    const sourceIds = sources.map((item) => item.id);
    const params = [
      {
        key: "sportId",
        value: sport.id,
      },
      {
        key: "take",
        value: 2000,
      }
    ];
    sourceIds.forEach((item) => params.push({ key: 'sources', value: item }));
    const venues = await fetchAllVenues(params);
    createVenuesHash(venues.data);
  }, [sport, sources, createVenuesHash]);

  const handleAddRow = useCallback(() => {
    const id = uuidv4();
    const items = {};
    sources.forEach((source) => items[source.id] = { id: null, source: source.id, unionKey: id });
    setApiConfigurationHash({ ...apiConfigurationHash, [id]: items });
  }, [apiConfigurationHash, sources]);

  const handleDeleteRow = useCallback((unionKey) => {
    const configuration = { ...apiConfigurationHash };
    delete configuration[unionKey];
    setApiConfigurationHash(configuration);
  }, [apiConfigurationHash]);

  const handleChange = useCallback((unionKey, source, league) => {
    const updatedItem = { ...apiConfigurationHash[unionKey][source], id: league?.id || null };
    setApiConfigurationHash({ ...apiConfigurationHash, [unionKey]: { ...apiConfigurationHash[unionKey], [source]: updatedItem } });
  }, [apiConfigurationHash]);

  const handleSave = useCallback(async () => {
    const data = [];
    for (let key in apiConfigurationHash) {
      const currentData = apiConfigurationHash[key];
      const dataArray = [];
      for (let sourceKey in currentData) {
        const sourceData = currentData[sourceKey];
        if (sourceData.id !== null) {
          dataArray.push(sourceData);
        }
      }
      if (dataArray.length > 0) {
        data.push(dataArray);
      }
    }
    await mergeVenues(sport.id, { items: data });
    toast.success('API Configuration saved successfully!');
  }, [apiConfigurationHash, sport.id]);

  const validateAiRow = useCallback((row, matchedIdsHash) => {
    if (row.items.length > sources.length) {
      return false;
    }
    const sourcesAmount = {};
    for (let i = 0; i < row.items.length; i++) {
      const item = row.items[i];
      if (!venuesHashBySource[item?.source][item?.id]) {
        return false;
      }
      if (matchedIdsHash[item.id]) {
        return false;
      }
      if (!sourcesAmount[item.source]) {
        sourcesAmount[item.source] = 1;
      } else {
        sourcesAmount[item.source] += 1;
      }
      if (sourcesAmount[item.source] > 1) {
        return false;
      }
    }
    return true;
  }, [venuesHashBySource, sources]);

  const handleAiMatch = useCallback(async () => {
    const { status, matches } = await matchVenuesByAi(sport.id);
    if (status === MATCH_RESPONSE_STATUS.INPROGRESS) {
      await timeout(5000);
      return handleAiMatch();
    }

    if (status === MATCH_RESPONSE_STATUS.FAILED) {
      return toast.error('Servers are busy. Try again later!');
    }
    const matchedRows = {};
    const matchedIdsHash = {};
    matches.forEach((row) => {
      const isValid = validateAiRow(row, matchedIdsHash);
      if (!isValid) {
        return;
      }
      const id = uuidv4();
      matchedRows[id] = {};
      row.items.forEach((item) => {
        matchedRows[id][item.source] = {
          id: item.id,
          source: item.source,
          unionKey: null,
          unionType: 'Ai',
          accuracy: row.accuracy
        };
        matchedIdsHash[item.id] = item;
      });
      sources.forEach((source) => {
        if (!matchedRows[id][source.id]) {
          matchedRows[id][source.id] = {
            id: null,
            source: source.id,
            unionKey: null,
            unionType: null,
          }
        }
      });
    });
    setApiConfigurationHash(matchedRows);
  }, [sources, sport.id, validateAiRow]);

  const handleAiMatchClick = useCallback(async () => {
    try {
      setAiMatchLoading(true);
      await handleAiMatch();
    } finally {
      setAiMatchLoading(false);
    }
  }, [handleAiMatch]);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setDialogData(null);
  }, []);

  const handleCloseClick = useCallback(() => {
    setIsDialogOpen(true);
    setDialogData({
      title: 'Confirm close',
      content: 'Are you sure you want to close? Changes won`t be saved.',
      onConfirm: () => {
        onClose();
        handleCloseDialog();
      },
      onClose: () => handleCloseDialog(),
    })
  }, [handleCloseDialog, onClose]);

  useEffect(() => {
    setupVenues();
  }, [setupVenues]);

  return (
    <Box sx={{ height: 'calc(100vh - 275px)' }}>
      {dialogData && <ConfirmationDialog open={isDialogOpen} title={dialogData.title} onClose={dialogData.onClose} onConfirm={dialogData.onConfirm} content={dialogData.content} />}
      <ConfigurationApiTable
        sources={sources}
        itemsHashBySource={venuesHashBySource}
        apiConfigurationHash={apiConfigurationHash}
        onChange={handleChange}
        onDeleteRow={handleDeleteRow}
        autocompleteLabel={'Venue'}
        onAddRow={handleAddRow}
        onAiMatch={handleAiMatchClick}
        onSave={handleSave}
        onClose={handleCloseClick}
        aiMatchLoading={aiMatchLoading}
        showFilter
      />
    </Box>
  )
}

export default ConfigurationApiVenues;
